import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import API from '../../../services/api';
import SearchBox from '../../../components/searchBox';
import CustomButton from '../../../components/customButton';
import { getUser } from '../../../redux/user/actions';
import './styles.scss';
import InviteUserModal from '../modal/inviteUserModal';
import { setAlert } from '../../../redux/dashboard/actions';
import utils from '../../../utils';
import ActionButtons from '../../../components/actionButtons';
import DeleteUserModal from '../modal/deleteUserModal';

const PortalUsers = ({ portal }) => {
  const [userList, setUserList] = useState([]);
  const [ownerUser, setOwnerUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const user = useSelector(getUser);

  const dispatch = useDispatch();

  useEffect(() => {
    if (portal) {
      loadUsers(portal.id);
    }
  }, [portal]);

  const loadUsers = async portalId => {
    try {
      const res = await API.getPortalPolicy(portalId);
      if (res && res.bindings) {
        const bindings = res.bindings || [];
        // collect members from bindings with role 'brand'
        const ownerBinding = bindings.find(b => b.role === 'owner');
        if (ownerBinding && ownerBinding.members && ownerBinding.members.length > 0) {
          const owner = ownerBinding.members[0];
          setOwnerUser({ owner });
        }
        const brandBindings = bindings.filter(b => b.role === 'brand');
        const members = brandBindings.reduce((acc, b) => acc.concat(b.members || []), []);
        setUserList(members);
        setSearchedUsers(members);
      } else {
        setUserList([]);
        setSearchedUsers([]);
      }
    } catch (err) {
      setUserList([]);
      setSearchedUsers([]);
      console.error('Error loading portal users', err);
    }
  };

  const onSearch = searchBy => {
    if (searchBy !== '') {
      const searchedUsers = userList.filter(user => {
        const userHas = option => user[option]?.toUpperCase()?.includes(searchBy.toUpperCase());
        return userHas('displayName') || userHas('email') || userHas('userId');
      });
      setSearchedUsers(searchedUsers);
    } else {
      setSearchedUsers(userList);
    }
  };

  const inviteUsers = (portalId, users) => {
    if (!portalId) return;
    setIsLoading(true);
    const invitePromises = users.map(user =>
      API.inviteUserToPortal(portalId, { email: user.email, role: 'admobilize.brand' })
    );

    Promise.allSettled(invitePromises)
      .then(results => {
        results.forEach(res => {
          if (res.status === 'fulfilled') {
            dispatch(setAlert(utils.generateAlert(`Successfully invited users.`, 'success')));
          } else if (res.status === 'rejected') {
            dispatch(setAlert(utils.generateAlert(`Error inviting users.`, 'error')));
            console.error('Error inviting user to portal:', res.reason);
          }
        });
      })
      .catch(error => {
        console.error('Error processing user invitations:', error);
      })
      .finally(() => {
        setIsLoading(false);
        setIsModalOpen(false);
      });
  };

  const removeUser = user => {
    const newUsers = userList.filter(u => u.userId !== user.userId);
    const members = [];
    newUsers.forEach(user => {
      members.push({
        email: user.email,
        displayName: user.displayName,
        userId: user.userId
      });
    });
    const body = {
      bindings: [
        {
          members: ownerUser ? [ownerUser.owner] : [],
          role: 'owner'
        },
        {
          members,
          role: 'brand'
        }
      ],
      resource: `brandportals/${portal.id}`
    };

    API.setPortalPolicy(portal.id, body)
      .then(() => {
        dispatch(setAlert(utils.generateAlert(`Successfully removed user.`, 'success')));
        loadUsers(portal.id);
      })
      .catch(err => {
        dispatch(setAlert(utils.generateAlert(`Error removing user.`, 'error')));
        console.error('Error removing user from portal:', err);
      })
      .finally(() => {
        setIsDeleteModalOpen(false);
        setUserToDelete(null);
      });
  };

  const renderAddUserButton = () => {
    return (
      user.id === portal?.createdBy && (
        <div className="add-invite-buttons">
          <CustomButton
            title="Invite Brand User"
            classes={'btn-add-user btn-primary'}
            handleClick={() => setIsModalOpen(true)}
          />
        </div>
      )
    );
  };

  const renderUserList = () => {
    if (!portal) {
      return <p>No users found.</p>;
    }
    if (isLoading) {
      return <p>Loading users...</p>;
    } else if (userList.length === 0) {
      return <p>No users found.</p>;
    }

    return searchedUsers.map(searchedUser => {
      return (
        <div className="user-wrapper d-flex w-100 justify-content-between align-items-center" key={searchedUser.userId}>
          <div className="user-data-row">
            <p className="user-name">{searchedUser.displayName ? searchedUser.displayName.toUpperCase() : null}</p>
            <p className="user-email">{searchedUser.email}</p>
          </div>
          <ActionButtons
            buttons={[
              {
                id: `remove-user`,
                icon: 'removeUser',
                key: 'Remove',
                tooltip: 'Remove User',
                onPress: () => {
                  setIsDeleteModalOpen(true);
                  setUserToDelete(searchedUser);
                }
              }
            ]}
          />
        </div>
      );
    });
  };

  const renderToolbar = () => (
    <>
      <div className="search-container">
        <SearchBox onChange={onSearch} placeholder={'Search User'} />
        <div>{renderAddUserButton()}</div>
      </div>
    </>
  );

  return (
    <div className="portal-users-section">
      {renderToolbar()}
      <div className="user-list-container">
        <label className="section-label">Invited Brand users</label>
        {renderUserList()}
      </div>
      {isModalOpen && (
        <InviteUserModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          cancelCallback={setIsModalOpen}
          confirmCallback={response => {
            inviteUsers(portal.id, response.users);
          }}
          isLoading={isLoading}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteUserModal
          isOpen={isDeleteModalOpen}
          cancelCb={() => setIsDeleteModalOpen(false)}
          confirmCb={() => {
            removeUser(userToDelete);
          }}
        />
      )}
    </div>
  );
};

export default PortalUsers;
